import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import classNames from "classnames";
import PropTypes from 'prop-types';
import axios from 'axios'
import {useErrorBoundary} from "use-error-boundary"
import _set from 'lodash/set';
import _mergeWith from "lodash/mergeWith";
import _cloneDeep from "lodash/cloneDeep";
import _debounce from "lodash/debounce";
import _isEqual from "lodash/isEqual";
import _findIndex from "lodash/findIndex";
import _isFunction from "lodash/isFunction";
import {arrayMove} from "@dnd-kit/sortable";
import {Transition} from "@headlessui/react";
import {plugins} from "./plugins/index";

import FormLayout from "./layouts/form-layout";
import FormContext from "./form-context";
import {
    applyDefaultValuesForExpressions,
    clearShowsWhenFieldValues,
    fieldsetsToObject,
    getFormFields,
    getUserDefinedFieldsets,
    getValuesIncludingHidden,
    mergeFieldsets,
    validate
} from "./utils";
import _isObject from "lodash/isObject";
import {WebsiteContext} from "../../websites";
import _get from "lodash/get";

const getFormAction = (e, submitter) => {
    return submitter?.getAttribute('formaction') || e.target.getAttribute('action') || null
}

const mergeComponentValues = (previousValues, nextValues) => _mergeWith(
    {},
    previousValues,
    nextValues,
    (_previousValue, nextValue) => Array.isArray(nextValue) ? nextValue : undefined
);

const getFieldId = (field) => field?.id || field?.name;

const onSubmitForm = (e, values, setContext, submitter) => {
    const form = e.target;

    return axios
        .post(getFormAction(e, submitter), values)
        .then((result) => {
            setContext(previous => ({...previous, progress: null}));
            form.dispatchEvent(new CustomEvent('cx:form-submitted', {
                bubbles: true, cancelable: true, detail: result
            }));
        })
        .catch((err) => {
            setContext(previous => ({...previous, progress: null}));
            if (err.isAxiosError) {
                switch (err.response.status) {
                    case 409:
                    case 422:
                        // Here we need to set the field(s) custom validity
                        getFormFields(form)
                            .forEach((element) => {
                                // If the element has a name and is not valid then update the fieldsets state
                                if (err.response.data[element.name] !== null && err.response.data[element.name] !== undefined) {
                                    if (err.response.data[element.name].length) {
                                        element.setCustomValidity(err.response.data[element.name].map(message => message.trim()).join('. '));
                                    } else {
                                        element.setCustomValidity(`${element.placeholder.trim()} is invalid`)
                                    }
                                }
                            });

                        validate(e.target, setContext, e.nativeEvent.detail?.autoSave);
                        break;
                    default:
                        console.log(err);
                }
            } else {
                console.log(err);
            }
        });
}

const getFormBody = (fieldsets, hiddenFields, values, userDefinedFieldsetsScope, includeAllDefinitions = false) => {
    const body = {};
    Object.keys(hiddenFields).forEach(key => _set(body, key, hiddenFields[key]));
    Object.assign(body, _cloneDeep(values));

    const userDefinedFieldsets = getUserDefinedFieldsets(fieldsets, includeAllDefinitions);
    if (userDefinedFieldsets) {
        if (userDefinedFieldsetsScope) {
            _set(body, `${userDefinedFieldsetsScope}.userDefinedFieldsets`, userDefinedFieldsets);
        } else {
            _set(body, 'userDefinedFieldsets', userDefinedFieldsets);
        }
    }

    // Clear fields where they are hidden by showsWhen expressions
    clearShowsWhenFieldValues(fieldsets, body);
    return body;
}

Form.propTypes = {
    id: PropTypes.string,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    noValidate: PropTypes.bool,
    autoSaveAfter: PropTypes.number,
    heading: PropTypes.oneOfType([PropTypes.bool, PropTypes.string,]),
    extendable: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({
            canChangeFieldName: PropTypes.bool,
            copy: PropTypes.shape({
                fieldset: PropTypes.object,
                field: PropTypes.object,
            }),
            controls: PropTypes.arrayOf(
                // FieldBuilder OPTIONS for all available options
                PropTypes.oneOf([
                    "text", "textarea", "currency", "checkbox", "switch", "select", "radio",
                    "rich-text", "image-uploader", "video-uploader", "document-uploader", "uploader"
                ])
            )
        })
    ]),
    sortableFieldsets: PropTypes.bool,
    layout: PropTypes.oneOf(['simple-stacked', 'two-column', 'panel-style', 'product-search-block', 'website', 'filter-panel']),
    actions: PropTypes.array,
    submittingState: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.element, PropTypes.elementType,]),
    hiddenFields: PropTypes.object,
    fieldsets: PropTypes.array,
    userDefinedFieldsets: PropTypes.array,
    onUserDefinedFieldsetsChange: PropTypes.func,
    userDefinedFieldsetsScope: PropTypes.string,
    /**
     * When provided the form fields will detect changes and if the field is different then shows as a modified state
     */
    original: PropTypes.object,
    inset: PropTypes.bool,
    fileUploadSettings: PropTypes.shape({
        saveUrl: PropTypes.string, basePath: PropTypes.string,
    }),
    /**
     * Prevents field definition updates from replacing the current draft values.
     * Consumers must provide resetKey when the external record changes.
     */
    preserveValuesOnFieldsetChange: PropTypes.bool,
    resetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hrefUriParams: PropTypes.object,
    customControls: PropTypes.object,
    onSubmit: PropTypes.func,
    onPrepareSubmit: PropTypes.func,
    onProcessingChange: PropTypes.func,
    onFieldChange: PropTypes.func,
    onComponentUpdate: PropTypes.func,
    onActionClick: PropTypes.func,
    onFieldsetSettingsUpdated: PropTypes.func,
    onFieldsetSettingsAdded: PropTypes.func,
    onFieldsetSettingsDeleted: PropTypes.func,
    paymentFieldsDisabledReason: PropTypes.string
};

Form.defaultProps = {
    extendable: false,
    layout: 'two-column',
    inset: false,
    actions: [],
    hiddenFields: {},
    fieldsets: [],
    userDefinedFieldsets: [],
    userDefinedFieldsetsScope: '',
    onSubmit: () => {},
    onPrepareSubmit: () => {},
    onProcessingChange: () => {},
    onFieldChange: () => {},
    onComponentUpdate: () => {},
    onActionClick: () => {},
    onFieldsetSettingsUpdated: () => {},
    onFieldsetSettingsAdded: () => {},
    onFieldsetSettingsDeleted: () => {},
    onFieldSettingsAction: () => {},
    onEditFieldsetSettings: () => {},
    preserveValuesOnFieldsetChange: false,
    resetKey: null,
};

Form.CustomControls = {};
// The host supplies its existing dialog without coupling this package to the admin app.
Form.ConfirmationDialog = null;

const PROCESSORS = {};

export default function Form({id, className, onSubmit, original, children, enabledPlugins, appContext, websiteManagementContext, selectedNode, preserveValuesOnFieldsetChange, resetKey, ...props}) {
    const {
        ErrorBoundary, didCatch
    } = useErrorBoundary();
    const websiteContext = useContext(WebsiteContext);
    const formRef = useRef();
    const initialRender = useRef(true);
    const submitInFlight = useRef(false);
    const lastResetKey = useRef(resetKey);
    const autoSaveFn = useMemo(() => {
        if (props.autoSaveAfter > 0) {
            return _debounce(() => {
                if (formRef.current) {
                    const event = new Event('submit', {bubbles: true});
                    event.detail = {autoSave: true};
                    formRef.current.dispatchEvent(event);
                }
            }, props.autoSaveAfter)
        } else {
            return () => {
            };
        }
    }, [props.autoSaveAfter]);

    const memoFieldsets = useMemo(() => {
        return mergeFieldsets(props.fieldsets, props.userDefinedFieldsets, props.userDefinedFieldsetsScope, (props.extendable && !props?.canEdit))
    }, [props.fieldsets, props.userDefinedFieldsets, props.userDefinedFieldsetsScope]);

    const memoValues = useMemo(() => {
        return getValuesIncludingHidden(memoFieldsets, props.hiddenFields, props?.parentContext?.values);
    }, [memoFieldsets, props.hiddenFields]);

    const memoErrors = useMemo(() => {
        return fieldsetsToObject(memoFieldsets, 'error')
    }, [memoFieldsets]);

    const customControls = useMemo(() => ({
        ...Form.CustomControls,
        ...(props.customControls || {})
    }), [props.customControls]);

    const [scrollPosition, setScrollPosition] = useState();
    const [fieldsets, setFieldsets] = useState(memoFieldsets);
    const [context, setContext] = useState(() => {
        return {
            id,
            layout: props.layout,
            hrefUriParams: props.hrefUriParams,
            submittingState: props.submittingState,
            fileUploadSettings: props.fileUploadSettings,
            original,
            values: _cloneDeep(props.values) || memoValues,
            errors: memoErrors,
            showChildForm: false,
            childForm: null,
            fieldsets: props.fieldsets,
            customFields: fieldsets,
            appContext,
            paymentFieldsDisabledReason: props.paymentFieldsDisabledReason,
            websiteContext,
            websiteManagementContext,
            selectedNode,
            customControls,
            parentContext: props.parentContext,
            validate: (ref) => {
                return validate((ref || formRef).current, setContext);
            },
            registerProcessor: (handler) => {
                PROCESSORS[id] = PROCESSORS[id] || [// A no op processor that kicks off the loading state
                    (onProgress) => new Promise((resolve) => {
                        onProgress(0);
                        resolve()
                    })];
                // Use a unique wrapper for this registration. Removing by array position is unsafe because another
                // processor may be removed first, shifting every later index.
                const processor = (...args) => handler(...args);
                PROCESSORS[id].push(processor);
                return {
                    destroy: () => {
                        const index = PROCESSORS[id]?.indexOf(processor) ?? -1;
                        if (index > -1) PROCESSORS[id].splice(index, 1);
                    },
                };
            },
            setContext: (values) => {
                setContext(_isFunction(values) ? values : (previous => ({...previous, ...values})));
            },
        }
    });

    useEffect(() => {
        plugins(websiteContext?.enabledPlugins || enabledPlugins).then(data => {
            context.setContext({pluginControls: data})
        })
    }, []);

    // Handle when the fieldsets or values prop has changed
    useEffect(() => {
        if (!initialRender.current) {
            const shouldPreserveValues = preserveValuesOnFieldsetChange && resetKey === lastResetKey.current;
            const nextValues = _cloneDeep(props.values) || memoValues;
            setFieldsets(memoFieldsets);
            setContext(previous => ({
                ...previous, ...({
                    original,
                    customControls,
                    ...(shouldPreserveValues
                        ? {}
                        : {values: nextValues}),
                    errors: memoErrors
                })
            }));
        } else {
            initialRender.current = false;
        }
        lastResetKey.current = resetKey;
    }, [memoFieldsets, props.values || memoValues, memoErrors, customControls, preserveValuesOnFieldsetChange, resetKey]);

    useEffect(() => {
        const initial = _cloneDeep(context.values);

        setContext(previous => ({...previous, customFields: fieldsets}));

        const userDefinedFieldsets = getUserDefinedFieldsets(fieldsets, props.sortableFieldsets);
        props.onUserDefinedFieldsetsChange?.(userDefinedFieldsets);
        formRef.current?.dispatchEvent(new CustomEvent('cx:form-user-defined-fieldsets-changed', {
            bubbles: true, cancelable: true, detail: userDefinedFieldsets
        }));

        const eventListener = (e) => {
            // Stops the native alert from showing if you're on a Suttons website or channel.
            const preventNativeAlert = !!(context.values?.website?.customProperties?.locationCode || context.values?.channel)
            const isDirty = !_isEqual(initial, context.values);

            if (isDirty && !preventNativeAlert) {
                e.preventDefault();
                return e.returnValue = 'Your changes will be lost. You should Save before navigating away.'
            }
        };

        window.addEventListener('beforeunload', eventListener);

        return () => {
            window.removeEventListener('beforeunload', eventListener);
        };
    }, [fieldsets]);

    useEffect(() => {
        setContext(previous => ({...previous, fileUploadSettings: props.fileUploadSettings}));
    }, [props.fileUploadSettings]);

    useEffect(() => {
        setContext(previous => ({
            ...previous,
            paymentFieldsDisabledReason: props.paymentFieldsDisabledReason
        }));
    }, [props.paymentFieldsDisabledReason]);

    useEffect(() => {
        const scrollContainer = formRef?.current?.closest('.overflow-y-auto');
        if (scrollContainer && context.showChildForm) setScrollPosition(scrollContainer.scrollTop); else if (scrollContainer && !context.showChildForm && scrollPosition) {
            setTimeout(() => {
                scrollContainer.scroll({
                    top: scrollPosition, behavior: 'auto'
                })
            }, 100)
            setScrollPosition()
        }
    }, [context.showChildForm]);

    const primaryAction = props.actions.find(action => action.primary) || props.actions[0];
    return (<>
        {didCatch ? (<p role="alert">This form could not be displayed. Reload the page and try again.</p>) : (<ErrorBoundary>
            <FormContext.Provider value={{...context,
                fieldControl: props.fieldControl,
                onFieldIntent: props.onFieldIntent,
                onFieldControlAction: props.onFieldControlAction,
                replaceFieldValues: (patch) => setContext(previous => {
                    const values = _cloneDeep(previous.values);
                    Object.entries(patch).forEach(([name, value]) => _set(values, name, _cloneDeep(value)));
                    return {...previous, values};
                }),
            }}>
                <Transition
                    show={!context.showChildForm}
                    unmount={false}
                    className={classNames("flex flex-col w-full h-full", props.forceFlex ? "!flex" : "flex")}
                    enter={"transition-transform duration-500 ease-[cubic-bezier(0,0.55,0.45,1)]"}
                    enterFrom="-translate-x-full sm:-translate-x-full"
                    enterTo="translate-x-0 sm:translate-x-0"
                    leave={"transition-transform duration-500 ease-[cubic-bezier(0,0.55,0.45,1)]"}
                    leaveFrom="translate-x-0 sm:translate-x-0"
                    leaveTo="-translate-x-full sm:-translate-x-full">
                    <form
                        id={id}
                        data-form=""
                        ref={formRef}
                        action={primaryAction?.href}
                        className={classNames("relative w-full m-0 p-0", props.containerClassName)}
                        onSubmit={e => {
                            e.preventDefault();
                            e.stopPropagation();

                            // State-driven disabled buttons do not update until React renders. Lock synchronously so
                            // a double click, Enter key repeat, or programmatic submit cannot start two processors.
                            if (submitInFlight.current) return;

                            // Prevents form from submitting again 
                            if (_isFunction(autoSaveFn.cancel)) autoSaveFn.cancel();

                            let submitter = e.nativeEvent.submitter;

                            if (!e.nativeEvent.detail?.autoSave) {
                                // If there is no submitter then try find the submit button as a child of the form
                                if (!submitter) {
                                    submitter = e.target.querySelector(`button[type='submit']`);
                                }

                                // If there is no submitter and there is an id for this form then try find a submit 
                                // button with the form attribute that matches the id of this form
                                if (id && !submitter) {
                                    submitter = document.querySelector(`button[form='${id}']`);
                                }
                            }

                            const isValid = validate(e.target, setContext, e.nativeEvent.detail?.autoSave);
                            if (!isValid) return;

                            submitInFlight.current = true;
                            props.onProcessingChange(true);

                            const values = context.values;
                            Promise.resolve()
                                // Consumers can add server-verifiable submission context before processors such as
                                // payment collection run. A returned object is treated as a values patch.
                                .then(() => props.onPrepareSubmit(e, values))
                                .then((preparedValues) => {
                                    if (_isObject(preparedValues) && preparedValues !== values) {
                                        _mergeWith(values, preparedValues);
                                    }

                                    const processors = [...(PROCESSORS[id] || [])];
                                    if (processors.length <= 1) return;

                                    return Promise.all(processors.map(processor => {
                                        // Starting from an already-resolved promise also captures synchronous throws
                                        // so the in-flight lock is always released by the common finally handler.
                                        return Promise.resolve()
                                            .then(() => processor((progress) => {
                                                setContext(previous => ({...previous, progress}));
                                            }, processors.length, values))
                                            .then((updatedFormValues) => {
                                                if (_isObject(updatedFormValues)) {
                                                    _mergeWith(values, updatedFormValues);
                                                }
                                            });
                                    }));
                                })
                                .then(() => {
                                    // Clean up processes
                                    delete PROCESSORS[id];

                                    const body = getFormBody(fieldsets, props.hiddenFields, values, props.userDefinedFieldsetsScope, props.sortableFieldsets);

                                    if (props.extendable || props.cleanUserDefinedFieldsets) {
                                        let userDefinedFieldsets = props.userDefinedFieldsetsScope ? _get(body, `${props.userDefinedFieldsetsScope}.userDefinedFieldsets`) : _get(body, 'userDefinedFieldsets');

                                        userDefinedFieldsets = userDefinedFieldsets?.reduce((result, fieldset) => {
                                            fieldset.fields = fieldset?.fields?.map(f => {
                                                delete f.disabled;
                                                return {...f, editing: false}
                                            })
                                            result.push({...fieldset, editing: false})
                                            return result;
                                        }, [])

                                        props.userDefinedFieldsetsScope ? _set(body, `${props.userDefinedFieldsetsScope}.userDefinedFieldsets`, userDefinedFieldsets) : _set(body, 'userDefinedFieldsets', userDefinedFieldsets);
                                    }

                                    if (getFormAction(e, submitter)) {
                                        return onSubmitForm(e, body, setContext, submitter);
                                    } else {
                                        e.target.dispatchEvent(new CustomEvent('cx:form-submitted', {
                                            bubbles: true, cancelable: true, detail: body
                                        }));

                                        return onSubmit(e, body, (progress) => {
                                            setContext(previous => ({...previous, progress}));
                                        })
                                    }
                                })
                                // Processors surface their own customer-facing error. A rejection deliberately
                                // stops the submit chain, so do not turn it into an unhandled browser rejection.
                                .catch(() => {})
                                .finally(() => {
                                    submitInFlight.current = false;
                                    props.onProcessingChange(false);
                                    setContext(previous => ({...previous, progress: null}));
                                })
                        }}
                        noValidate={props.noValidate === true ? true : null}
                    >
                        <FormLayout
                            {...props}
                            fieldsets={fieldsets}
                            onFieldsetSortEnd={({oldIndex, newIndex}) => {
                                if (!Number.isInteger(oldIndex) || !Number.isInteger(newIndex)
                                    || oldIndex < 0 || newIndex < 0 || oldIndex >= fieldsets.length || newIndex >= fieldsets.length
                                    || fieldsets[oldIndex].system || fieldsets[newIndex].system) return;
                                setFieldsets(previous => arrayMove(previous, oldIndex, newIndex));
                            }}
                            className={classNames(props.inset ? "sm:mx-6 p-6 rounded-md bg-panels-100 m-4" : null, className)}
                            onEditFieldsetSettings={(e, index, editing) => {
                                setFieldsets(previous => {
                                    const fieldsets = [...previous];
                                    fieldsets[index].editing = editing;
                                    return fieldsets;
                                });
                                props.onEditFieldsetSettings(e, index, editing)
                            }}
                            onFieldsetSettingsAdded={(e, fieldset) => {
                                setFieldsets([...fieldsets, fieldset]);
                                props.onFieldsetSettingsAdded(e, fieldset);
                            }}
                            onFieldsetSettingsUpdated={(e, index, fieldset) => {
                                setFieldsets(previous => {
                                    const fieldsets = [...previous];
                                    fieldsets[index] = {...fieldset, editing: false};

                                    return fieldsets;
                                });
                                props.onFieldsetSettingsUpdated(e, index, fieldset);
                            }}
                            onFieldsetSettingsDeleted={(e, index) => {
                                const deletedFieldset = fieldsets[index];
                                setFieldsets(previous => {
                                    const fieldsets = [...previous];
                                    fieldsets.splice(index, 1);
                                    return fieldsets;
                                });
                                props.onFieldsetSettingsDeleted(e, index, deletedFieldset);
                            }}
                            onEditFieldSettings={(e, field, fieldset) => {
                                setFieldsets(previous => {
                                    const fieldsets = [...previous];

                                    // Find the fieldset and shallow clone it
                                    let fsIndex = _findIndex(fieldsets, {id: fieldset.id});
                                    fieldsets[fsIndex] = {...fieldsets[fsIndex]};

                                    // Find the field in the found fieldset and shallow clone it setting the editing
                                    // state
                                    let fIndex = _findIndex(fieldsets[fsIndex].fields, {id: field.id});
                                    fieldsets[fsIndex].fields[fIndex] = {
                                        ...fieldsets[fsIndex].fields[fIndex], editing: true
                                    }

                                    return fieldsets;
                                });
                            }}
                            onFieldSettingsAction={(e, action, values, field, fieldset) => {
                                // Shallow clone the fieldsets array
                                const newFieldsets = [...fieldsets];

                                // Find the fieldset and shallow clone it
                                let fsIndex = _findIndex(newFieldsets, {id: fieldset.id});
                                newFieldsets[fsIndex] = {...newFieldsets[fsIndex]};

                                // Shallow clone the fields array on the fieldset
                                const fields = [...newFieldsets[fsIndex].fields];

                                // Find the field we are managing
                                const index = _findIndex(fields, {id: field?.id});
                                if (index > -1) {
                                    // Update the field settings
                                    if (action.primary) {
                                        fields[index] = values;
                                    }
                                    // Remove field
                                    else if (action.destructive) {
                                        fields.splice(index, 1)
                                    }
                                    // Ignore changes and revert editing state
                                    else {
                                        fields[index].editing = false;
                                    }
                                }
                                // Add the new field
                                else if (action.primary) {
                                    fields.push(values);
                                }

                                newFieldsets[fsIndex].fields = fields;
                                setFieldsets(newFieldsets);
                                if (newFieldsets) {
                                    const userDefinedFieldsets = getUserDefinedFieldsets(newFieldsets, props.sortableFieldsets);
                                    setContext(previous => {
                                        const nextValues = _cloneDeep(previous.values || {});
                                        if (props.userDefinedFieldsetsScope) {
                                            _set(nextValues, `${props.userDefinedFieldsetsScope}.userDefinedFieldsets`, userDefinedFieldsets);
                                        } else {
                                            nextValues.userDefinedFieldsets = userDefinedFieldsets;
                                        }
                                        return {...previous, values: nextValues};
                                    });
                                }
                                props.onFieldSettingsAction(e, newFieldsets, context.values);
                            }}
                            onFieldSortEnd={({oldIndex, newIndex, fieldset}) => {
                                const fields = [...(fieldset?.fields || [])];
                                const isSortableField = (field) => !field?.system || field?.contactField === true;
                                const sortableFields = fields.filter(isSortableField);
                                const safeOldIndex = Number.isInteger(oldIndex) ? oldIndex : -1;
                                const safeNewIndex = Number.isInteger(newIndex) ? newIndex : -1;
                                if (
                                    safeOldIndex < 0 || safeNewIndex < 0 ||
                                    safeOldIndex >= sortableFields.length || safeNewIndex >= sortableFields.length
                                ) {
                                    return;
                                }

                                const nextSortableFields = arrayMove(sortableFields, safeOldIndex, safeNewIndex);
                                let sortableIndex = 0;
                                const nextFields = fields.map(field => {
                                    if (!isSortableField(field)) return field;
                                    const nextField = nextSortableFields[sortableIndex];
                                    sortableIndex += 1;
                                    return nextField;
                                });

                                const isContactField = (field) => field?.contactField === true;
                                const hasContactFields = nextFields.some(isContactField);
                                const shouldUpdateContactOrder = !!(fieldset?.contactFieldOrderPath && hasContactFields);
                                const updatedContactOrder = shouldUpdateContactOrder
                                    ? nextFields
                                        .filter(field => !(field?.system === true && field?.type === 'step'))
                                        .map(getFieldId)
                                        .filter(id => id !== undefined && id !== null && id !== '')
                                    : null;
                                let nextFieldsetsState = null;

                                setFieldsets(previous => {
                                    // Shallow clone the fieldsets array
                                    const fieldsets = [...previous];

                                    // Find the fieldset and shallow clone it
                                    let fsIndex = _findIndex(fieldsets, {id: fieldset.id});
                                    if (fsIndex < 0) return fieldsets;
                                    fieldsets[fsIndex] = {...fieldsets[fsIndex]};
                                    fieldsets[fsIndex].fields = nextFields;
                                    nextFieldsetsState = fieldsets;
                                    return fieldsets;
                                });

                                if (shouldUpdateContactOrder && fieldset?.contactFieldOrderPath) {
                                    const nextValues = _cloneDeep(context.values || {});
                                    _set(nextValues, fieldset.contactFieldOrderPath, updatedContactOrder || []);

                                    // Keep unsaved added/moved fields when parent listeners refresh values.
                                    // Without this, consumers can rehydrate stale userDefinedFieldsets and drop
                                    // fields that were just added then moved.
                                    if (nextFieldsetsState) {
                                        const userDefinedFieldsets = getUserDefinedFieldsets(nextFieldsetsState);
                                        if (props.userDefinedFieldsetsScope) {
                                            _set(nextValues, `${props.userDefinedFieldsetsScope}.userDefinedFieldsets`, userDefinedFieldsets);
                                        } else {
                                            nextValues.userDefinedFieldsets = userDefinedFieldsets;
                                        }
                                    }

                                    setContext(previous => ({...previous, values: nextValues}));
                                    if (props.onFieldChange) {
                                        props.onFieldChange(null, fieldset.contactFieldOrderPath, updatedContactOrder || [], nextValues);
                                    }
                                }
                            }}
                            onComponentUpdate={(e, newComponent, ignoreAutoSave) => {
                                // Clear custom validation when the field has changed
                                if (e?.target?.setCustomValidity) {
                                    e.target.setCustomValidity('');
                                }

                                setContext(previous => {
                                    const result = {
                                        ...previous,
                                        values: mergeComponentValues(previous.values, newComponent),
                                        errors: {...previous.errors},
                                    };

                                    // Call this handler on the next tick to give time for the form to render
                                    // the new context change
                                    setTimeout(() => {
                                        props.onFieldChange(e, null, null, newComponent);

                                        // Invoke auto save
                                        if (!ignoreAutoSave) {
                                            autoSaveFn();
                                        }
                                    })

                                    return result;
                                });
                            }}
                            onFieldChange={(e, name, value, newConditionalForms, ignoreAutoSave) => {
                                // Clear custom validation when the field has changed
                                if (e?.target?.setCustomValidity) {
                                    e.target.setCustomValidity('');
                                }

                                setContext(previous => {
                                    const result = {
                                        ...previous, values: {...previous.values}, errors: {...previous.errors},
                                    };

                                    newConditionalForms
                                        ?.map(form => getValuesIncludingHidden(form.fieldsets, form.hiddenFields))
                                        .forEach(newConditionalFormValues => _mergeWith(result.values, newConditionalFormValues));

                                    _set(result.values, name, value);
                                    _set(result.errors, name, false);

                                    // Apply new defaults of fields that have expressions
                                    applyDefaultValuesForExpressions(fieldsets, name, value, result.values);

                                    // Call this handler on the next tick to give time for the form to render
                                    // the new context change
                                    setTimeout(() => {
                                        const body = getFormBody(fieldsets, props.hiddenFields, result.values, props.userDefinedFieldsetsScope);

                                        props.onFieldChange(e, name, value, body);

                                        // Invoke auto save
                                        if (!ignoreAutoSave) {
                                            autoSaveFn();
                                        }
                                    })

                                    return result;
                                });
                            }}
                            onActionClick={(e, action) => {
                                if (action.primary) return;

                                e.target.dispatchEvent(new CustomEvent('cx:form-action', {
                                    bubbles: true, cancelable: true, detail: action
                                }));

                                props.onActionClick(e, action);
                            }}
                        />
                        {children}
                    </form>
                </Transition>
                {context.childForm ? (<Transition
                    show={context.showChildForm === true}
                    appear={"true"}
                    className={"absolute z-10 inset-0 flex flex-col w-full h-full sm:mt-auto bg-white overflow-y-auto"}
                    enter={"transition-transform ease-[cubic-bezier(0,0.55,0.45,1)] duration-500"}
                    enterFrom="translate-x-full"
                    enterTo="translate-x-0"
                    leave={"transition-transform ease-[cubic-bezier(0,0.55,0.45,1)] duration-500"}
                    leaveFrom="translate-x-0"
                    leaveTo="translate-x-full"
                >
                    {context.childForm}
                </Transition>) : null}
            </FormContext.Provider>
        </ErrorBoundary>)}
    </>)
}
